import React, { useMemo, useState } from 'react'
import { Box, Divider, IconButton, Typography } from '@mui/material'
import type { ColumnDef } from '@tanstack/react-table'
import { TanStackTable } from '@/@core/components/mui/TanStackTable'
import { TablePagination, TablePaginationProps } from '@/@core/components/mui/TablePagination'
import { IUser, UserStatusEnum, useUpdateUserStatusMutation } from '@/@data'
import dayjs from 'dayjs'
import { UserAvatar } from '../../common/UserAvatar'
import UserStatusDropDown from '../UserStatusDropDown'
import { useNotification } from '@/hooks/useNotification'
import { Button } from '@/@core/components/mui/Button'
import { useRowSelection } from '@/@core/hooks'
import { useRouteQuery } from '@/hooks/useRouteQuery'
import { useTranslator } from '@/hooks'
import Link from '../../Link'
import GuestAuthEmployee from '@/components/user/modal/GuestAuthEmployee'
import { NoteTimelineDialog } from '@/components/user/modal/NoteTimelineDialog'
import { BulkingNoteDisableDialog } from '@/components/user/modal/BulkingNoteDisableDialog'
import { SelectField } from '../../common/input/SelectField'

interface EmployeeTableProps {
  data: IUser[]
  meta: unknown
  onRowClick?: (employee: IUser) => void
  onEdit?: (employee: IUser) => void
  refetch: () => void
  onRowSelectionChange?: (rowSelection: Record<string, boolean>) => void
}

const EmployeeTable: React.FC<EmployeeTableProps> = ({ data, meta, onEdit, refetch, onRowSelectionChange }) => {
  const { __ } = useTranslator()
  const { rowSelection, setRowSelection, selectedRows } = useRowSelection(data)
  const changeStatusMutation = useUpdateUserStatusMutation()

  React.useEffect(() => {
    onRowSelectionChange?.(rowSelection)
  }, [rowSelection, onRowSelectionChange])
  const [guestModalOpen, setGuestModalOpen] = useState(false)
  const [isOpenNote, setIsOpenNote] = useState(false)
  // State for note dialog
  const [noteDialogOpen, setNoteDialogOpen] = useState(false)
  const [selectedEmployee, setSelectedEmployee] = useState<IUser | undefined>(undefined)
  const [selectedEmployees, setSelectedEmployees] = useState<IUser[]>([])
  const n = useNotification()
  const { searchQuery } = useRouteQuery()
  const [selectedStatus, setSelectedStatus] = useState<UserStatusEnum>(UserStatusEnum.ACTIVE)

  const columns = useMemo<ColumnDef<IUser>[]>(
    () => [
      {
        accessorKey: 'employee',
        header: __('Employees'),
        size: 250,
        cell: ({ row }) => {
          return (
            <UserAvatar
              user={row.original}
              link={`/cms/users/employee/${row.original.id}?month=${row.original?.company?.current_pay_period}`}
            />
          )
        }
      },
      {
        accessorKey: 'company',
        header: __('Company'),
        cell: ({ row }) => (
          <Typography variant='body2' color='text.primary'>
            {row.original.company?.name || row.original.company_name}
          </Typography>
        )
      },
      {
        accessorKey: 'employee_id',
        header: __('Employee ID')
      },
      {
        accessorKey: 'created_at',
        header: __('Create Date'),
        cell: ({ row }) => (
          <Typography variant='body2' color='text.primary'>
            {dayjs(row.original.created_at).format('DD/MM/YYYY')}
          </Typography>
        )
      },
      {
        accessorKey: 'status',
        header: __('Status'),
        cell: ({ row }) => {
          const employee = row.original

          return employee.status === UserStatusEnum.GUEST || employee.status === UserStatusEnum.GUEST_WAITING ? (
            <Box sx={{ display: 'flex', gap: 4, alignItems: 'center' }}>
              <Button
                variant='contained'
                className='bg-[#6F83F6]'
                endIcon={<i className='tabler-link' />}
                sx={{ height: '30px', fontSize: '13px' }}
                onClick={() => {
                  setSelectedEmployee(employee)
                  setGuestModalOpen(true)
                }}
              >
                {employee.status === UserStatusEnum.GUEST_WAITING ? __('Guest Waiting') : __('Guest')}
              </Button>
              <IconButton
                size='large'
                className='bg-[#6F83F6] w-[38px] h-[28px] rounded-[20px]'
                onClick={() => {
                  setSelectedEmployee(employee)
                  setIsOpenNote(true)
                }}
              >
                <i className='tabler-note w-[10px] h-[16px] text-[#ffffff]' />
              </IconButton>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', gap: 4, alignItems: 'center' }}>
              <UserStatusDropDown
                status={employee.status}
                statusPrevious={employee.status_previous}
                onSelect={status => {
                  changeStatusMutation.mutate(
                    {
                      id: [employee.id],
                      status: status as UserStatusEnum
                    },
                    {
                      onSuccess: () => {
                        refetch()
                        n.success(__('Status updated successfully'))
                      },
                      onError: err => {
                        if (err.message) {
                          n.error(__(err.message))

                          return
                        }

                        n.error(__('Status update failed'))
                      }
                    }
                  )
                }}
                sx={{
                  height: '30px',
                  fontSize: '13px'
                }}
                excludeGuestStatuses={true}
              />
              {employee.status === UserStatusEnum.INACTIVE && (
                <IconButton
                  size='large'
                  className='bg-[#6F83F6] w-[38px] h-[28px] rounded-[20px]'
                  onClick={() => {
                    setSelectedEmployee(employee)
                    setIsOpenNote(true)
                  }}
                >
                  <i className='tabler-note w-[10px] h-[16px] text-[#ffffff]' />
                </IconButton>
              )}
            </Box>
          )
        }
      },
      {
        accessorKey: 'actions',
        header: __('Actions'),
        cell: ({ row }) => {
          return (
            <Link
              href={`/cms/users/employee/${row.original?.id}?month=${row.original?.company?.current_pay_period}`}
              className='flex items-center gap-4'
            >
              <i className='tabler-edit text-[#2F2B3DB2] size-[22px]' />
            </Link>
          )
        },
        size: 80
      }
    ],
    [onEdit]
  )

  return (
    <>
      {Object.keys(rowSelection).length > 0 && (
        <>
          <Divider />
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '20px'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <SelectField
                displayEmpty
                value={Object.keys(rowSelection).length > 0 ? 'Selected' : 'Un-Selected'}
                onChange={e => {
                  if (e.target.value === 'Un-Selected') {
                    setRowSelection({})
                  }
                }}
                options={[
                  { label: Object.keys(rowSelection).length + ' ' + __('Selected'), value: 'Selected' },
                  { label: __('Un-Selected'), value: 'Un-Selected' }
                ]}
                style={{ minWidth: '140px' }}
              />

              <UserStatusDropDown
                status={selectedStatus}
                onStatusChange={setSelectedStatus}
                excludeGuestStatuses={true}
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 3 }}>
              <Button
                variant='contained'
                size='small'
                startIcon={<i className='tabler-check' />}
                sx={{ height: 40 }}
                onClick={() => {
                  if (Object.keys(rowSelection).length > 0) {
                    if (selectedStatus === UserStatusEnum.INACTIVE) {
                      if (selectedRows && selectedRows.length > 0) {
                        setSelectedEmployees(selectedRows)
                        setNoteDialogOpen(true)

                        return
                      }
                    }

                    changeStatusMutation.mutate(
                      {
                        id: Object.keys(rowSelection).map(Number),
                        status: selectedStatus
                      },
                      {
                        onSuccess: () => {
                          refetch()
                          n.success(__('Status updated successfully'))
                        },
                        onError: (err) => {
                          if (err.message) {
                          n.error(__(err.message))
                          refetch()

                          return
                        }

                          n.error(__('Status update failed'))
                        }
                      }
                    )
                  }
                }}
                disabled={
                  Object.keys(rowSelection).length === 0 ||
                  selectedRows.some(
                    row => row.status === UserStatusEnum.GUEST || row.status === UserStatusEnum.GUEST_WAITING
                  )
                }
                loading={changeStatusMutation.isPending}
              >
                {__('Submit')}
              </Button>

              <Button
                variant='tonal'
                color='secondary'
                onClick={() => setRowSelection({})}
                startIcon={<i className='tabler-x' />}
              >
                {__('Clear')}
              </Button>
            </Box>
          </Box>
        </>
      )}

      <TanStackTable
        columns={columns}
        tableData={data}
        rowSelection={rowSelection}
        setRowSelection={setRowSelection}
        enableRowSelection
        borderRight='2px solid #e0e0e0'
      />

      {meta && (
        <TablePagination
          meta={
            {
              ...meta,
              page: Number(searchQuery.get('page')) || 1,
              perPage: Number(searchQuery.get('perPage')) || 10
            } as unknown as TablePaginationProps
          }
        />
      )}

      {selectedEmployee && (
        <>
          <GuestAuthEmployee
            open={guestModalOpen}
            onClose={() => {
              setGuestModalOpen(false)
              setSelectedEmployee(undefined)
            }}
            employee={selectedEmployee}
            refetch={refetch}
          />

          <NoteTimelineDialog
            open={isOpenNote}
            setOpen={open => {
              setIsOpenNote(open)

              if (!open) {
                setSelectedEmployee(undefined)
              }
            }}
            employee={selectedEmployee}
          />
        </>
      )}

      <BulkingNoteDisableDialog
        open={noteDialogOpen}
        setOpen={setNoteDialogOpen}
        dataRequest={selectedEmployees}
        onSuccess={() => {
          refetch()
        }}
      />
    </>
  )
}

export default EmployeeTable
